// Copyright (c) 2026 BlueRock Security, Inc.
// All rights reserved.
//
// BLUEROCK SECURITY, INC. PROPRIETARY AND CONFIDENTIAL INFORMATION

/**
 * The six viz renderers behind MetricCard. Internal — the public surface is
 * MetricCard itself.
 *
 * The board's design thesis is "now, against last period": time-aware cards
 * carry their Previous Window as quiet gray context (trend ghost line, named
 * deltas). Marks follow the dataviz specs — thin bars with rounded data-ends,
 * 2px lines with a subtle wash, 2px surface gaps between touching fills,
 * recessive hairline axes, and text in text tokens (never the series color).
 * Meter and composition are plain token-driven markup; ECharts only where a
 * real plot needs it (trend, bars).
 */

import { Flex, Typography, theme } from 'antd';
import type { EChartsOption } from 'echarts';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { cartesianDefaults, categoricalPalette, tooltipDefaults } from './chartTheme';
import { useEChart } from './useEChart';
import {
  formatMetricValue,
  statusForValue,
  type MetricCardDescriptor,
  type MetricEnvelope,
  type MetricGroup,
  type MetricPoint,
  type MetricStatus,
  type MetricUnit,
} from './types';

const { Text } = Typography;

interface VizProps {
  descriptor: MetricCardDescriptor;
  data: MetricEnvelope;
}

function statusColor(status: MetricStatus | undefined, token: ReturnType<typeof theme.useToken>['token']) {
  switch (status) {
    case 'good':
      return token.colorSuccess;
    case 'warning':
      return token.colorWarning;
    case 'critical':
      return token.colorError;
    default:
      return undefined;
  }
}

// ---------------------------------------------------------------------------
// stat — MetricCard's own stat body, rendered inside the shared card shell
// like every other viz (title left, window caption top-right — one chrome).

export function StatViz({ descriptor, data }: VizProps) {
  const { token } = theme.useToken();
  const opts = descriptor.viz.options ?? {};
  const scalar = data.scalar;
  const value = scalar ? formatMetricValue(scalar.value, data.unit) : '—';

  const status = scalar ? statusForValue(scalar.value, descriptor.thresholds) : undefined;
  const valueColor = statusColor(status, token) ?? token.colorTextHeading;

  let delta: DeltaFacts | undefined;
  if (opts.delta && scalar?.prev_value != null) {
    // Deltas name their comparison window ("now vs last period").
    const caption = descriptor.query?.window ? `vs prior ${descriptor.query.window}` : 'vs prior period';
    if (scalar.prev_value !== 0) {
      const pct = ((scalar.value - scalar.prev_value) / Math.abs(scalar.prev_value)) * 100;
      const up = pct >= 0;
      delta = {
        pct,
        good: opts.deltaGoodWhen === 'down' ? !up : up,
        // Trivial movement shouldn't shout.
        muted: Math.abs(pct) < 2,
        caption,
      };
    } else if (scalar.value !== 0) {
      // Percent-from-zero is undefined — call it what it is instead of
      // silently dropping the pill on a first active window.
      delta = { isNew: true, good: true, muted: true, caption };
    }
  }
  const spark = opts.spark && (data.spark?.length ?? 0) > 1 ? data.spark : undefined;

  return (
    <Flex vertical justify="center" gap={token.marginXS} style={{ flex: 1, minHeight: 0 }}>
      {/* Value line: figure baseline-anchored left, context sparkline riding
          the same line on the right (the SaaS canvas layout). */}
      <Flex align="flex-end" justify="space-between" gap={12}>
        <Text
          className="tabular-nums"
          style={{
            fontSize: token.fontSizeHeading2,
            fontWeight: 600,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: valueColor,
            flexShrink: 0,
          }}
        >
          {value}
        </Text>
        {spark && <Spark points={spark} unit={data.unit} hourly={data.window?.interval === '1h'} />}
      </Flex>
      {delta && (
        <Flex align="center" gap={token.marginXS}>
          <DeltaPill delta={delta} />
          <Text style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary }}>
            {delta.caption}
          </Text>
        </Flex>
      )}
    </Flex>
  );
}

interface DeltaFacts {
  pct?: number;
  good: boolean;
  muted: boolean;
  caption: string;
  /** Previous window was zero — a percent has no meaning. */
  isNew?: boolean;
}

/** Tinted rounded pill (SaaS treatment) — quiet fill for trivial movement. */
function DeltaPill({ delta }: { delta: DeltaFacts }) {
  const { token } = theme.useToken();
  const [bg, fg] =
    delta.muted || delta.isNew
      ? [token.colorFillTertiary, token.colorTextSecondary]
      : delta.good
        ? [token.colorSuccessBg, token.colorSuccessText]
        : [token.colorErrorBg, token.colorErrorText];
  return (
    <span
      className="tabular-nums"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 3,
        padding: `1px ${token.paddingXS}px`,
        borderRadius: 999,
        background: bg,
        color: fg,
        fontSize: token.fontSizeSM,
      }}
    >
      {delta.isNew ? (
        'new'
      ) : (
        <>
          {delta.pct! >= 0 ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
          {Math.abs(delta.pct!).toFixed(1)}%
        </>
      )}
    </span>
  );
}

/**
 * Context sparkline: gray line with only the final segment + end dot in
 * accent. An ECharts mini chart (not static SVG) so it stretches to the
 * space beside the value and hover reveals the underlying points.
 */
function Spark({ points, unit, hourly }: { points: MetricPoint[]; unit: MetricUnit; hourly: boolean }) {
  const { token } = theme.useToken();
  const vals = points.map(p => p.v);
  const last = vals.length - 1;
  const option: EChartsOption = {
    grid: { left: 2, right: 4, top: 4, bottom: 4 },
    tooltip: {
      ...tooltipDefaults(token),
      // One line to the reader — hide the gray/accent split and the series
      // duplication on the tail points.
      formatter: params => {
        const p = (Array.isArray(params) ? params[0] : params) as {
          axisValue?: string | number;
          value?: unknown;
        };
        return `${p.axisValue ?? ''} · ${formatMetricValue(Number(p.value ?? 0), unit)}`;
      },
    },
    xAxis: {
      type: 'category',
      show: false,
      boundaryGap: false,
      data: points.map(p => formatAxisTime(p.t, hourly)),
    },
    yAxis: { type: 'value', show: false },
    series: [
      {
        type: 'line',
        name: 'context',
        data: vals,
        showSymbol: false,
        lineStyle: { width: 1.25, color: token.colorTextQuaternary },
        itemStyle: { color: token.colorTextQuaternary },
        emphasis: { disabled: true },
      },
      {
        type: 'line',
        name: 'tip',
        // Accent only over the final segment; the end dot marks "now".
        data: vals.map((v, i) =>
          i === last ? { value: v, symbolSize: 5 } : i === last - 1 ? v : null
        ),
        showSymbol: true,
        symbolSize: 0,
        lineStyle: { width: 1.25, color: token.colorPrimary },
        itemStyle: { color: token.colorPrimary },
        emphasis: { disabled: true },
      },
    ],
  };
  const ref = useEChart(option);
  return (
    <div
      ref={ref}
      role="img"
      aria-label="sparkline"
      style={{ flex: 1, minWidth: 64, maxWidth: 180, height: 28, marginBottom: 2 }}
    />
  );
}

// ---------------------------------------------------------------------------
// meter — a single ratio against a limit (linear fill, never a radial gauge)

export function MeterViz({ descriptor, data }: VizProps) {
  const { token } = theme.useToken();
  const opts = descriptor.viz.options ?? {};
  const value = data.scalar?.value ?? 0;
  const min = opts.min ?? 0;
  const max = opts.max ?? 100;
  const ratio = Math.min(Math.max((value - min) / (max - min || 1), 0), 1);
  const fill = statusColor(statusForValue(value, descriptor.thresholds), token) ?? token.colorPrimary;

  return (
    <Flex vertical justify="center" gap={token.marginXS} style={{ flex: 1 }}>
      <Flex align="baseline" gap={6}>
        <Text style={{ fontSize: 22, fontWeight: 600, color: token.colorTextHeading }}>
          {formatMetricValue(value, data.unit)}
        </Text>
        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
          of {formatMetricValue(max, data.unit)}
        </Text>
      </Flex>
      <div
        role="meter"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        aria-label={descriptor.title}
        style={{
          height: 8,
          borderRadius: 4,
          // Track is a lighter step of the same ramp, so state reads across
          // the whole bar (dataviz meter spec) — brand-tinted, not gray.
          background: token.colorPrimaryBg,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${ratio * 100}%`,
            height: '100%',
            borderRadius: 4,
            background: fill,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// trend — current period in accent, Previous Window as a gray ghost

export function TrendViz({ descriptor, data }: VizProps) {
  const { token } = theme.useToken();
  const opts = descriptor.viz.options ?? {};
  const series = data.series ?? [];
  const ghost = data.prev_series ?? [];
  const axis = cartesianDefaults(token);
  const hourly = data.window?.interval === '1h';
  const latest = series.length ? series[series.length - 1].v : undefined;

  const option: EChartsOption = {
    grid: { left: 8, right: 44, top: 8, bottom: 0, containLabel: true },
    tooltip: {
      ...tooltipDefaults(token),
      valueFormatter: v => formatMetricValue(Number(v), data.unit),
    },
    xAxis: {
      type: 'category',
      data: series.map(p => formatAxisTime(p.t, hourly)),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: axis.axisLabelColor, fontSize: axis.fontSize },
    },
    yAxis: {
      type: 'value',
      splitNumber: 3,
      splitLine: { lineStyle: { color: axis.splitLineColor } },
      axisLabel: {
        color: axis.axisLabelColor,
        fontSize: axis.fontSize,
        formatter: (v: number) => formatMetricValue(v, data.unit),
      },
    },
    series: [
      // Ghost first so the current line draws over it. Indexed by position:
      // both series share the current window's category axis (equal length,
      // equal bucketing per the contract).
      ...(ghost.length
        ? [
            {
              type: 'line' as const,
              name: 'previous period',
              data: ghost.map(p => p.v),
              showSymbol: false,
              silent: true,
              lineStyle: { width: 1.5, color: token.colorTextQuaternary },
              itemStyle: { color: token.colorTextQuaternary },
              emphasis: { disabled: true },
            },
          ]
        : []),
      {
        type: 'line',
        name: 'current',
        data: series.map(p => p.v),
        showSymbol: false,
        lineStyle: { width: 2, color: token.colorPrimary },
        itemStyle: { color: token.colorPrimary },
        // Gradient wash (SaaS treatment): accent at the line fading to
        // nothing at the baseline, instead of a flat 10% fill.
        areaStyle: opts.area
          ? {
              opacity: 0.25,
              color: {
                type: 'linear' as const,
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: token.colorPrimary },
                  { offset: 1, color: 'transparent' },
                ],
              },
            }
          : undefined,
        // End dot with a surface ring + the latest value labeled at the line
        // end — the one number the card is about.
        markPoint:
          latest != null
            ? {
                symbol: 'circle',
                symbolSize: 8,
                itemStyle: { color: token.colorPrimary, borderColor: token.colorBgContainer, borderWidth: 2 },
                label: {
                  show: true,
                  position: 'right',
                  color: token.colorTextHeading,
                  fontSize: axis.fontSize,
                  fontWeight: 600,
                  formatter: () => formatMetricValue(latest, data.unit),
                },
                data: [{ name: 'latest', coord: [series.length - 1, latest] }],
              }
            : undefined,
      },
    ],
  };

  return <Chart option={option} ariaLabel={descriptor.title} />;
}

// ---------------------------------------------------------------------------
// bars — magnitude comparison / distributions

export function BarsViz({ descriptor, data }: VizProps) {
  const { token } = theme.useToken();
  const opts = descriptor.viz.options ?? {};
  const groups = data.groups ?? [];
  const axis = cartesianDefaults(token);

  const barColor = (g: MetricGroup) =>
    opts.colorMode === 'status'
      ? (statusColor(statusForValue(g.value, descriptor.thresholds), token) ?? token.colorPrimary)
      : token.colorPrimary;

  const option: EChartsOption = {
    grid: { left: 8, right: 8, top: 8, bottom: 0, containLabel: true },
    tooltip: {
      ...tooltipDefaults(token),
      valueFormatter: v => formatMetricValue(Number(v), data.unit),
    },
    xAxis: {
      type: 'category',
      data: groups.map(g => g.label),
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: axis.axisLabelColor, fontSize: axis.fontSize },
    },
    yAxis: {
      type: 'value',
      splitNumber: 3,
      splitLine: { lineStyle: { color: axis.splitLineColor } },
      axisLabel: { color: axis.axisLabelColor, fontSize: axis.fontSize },
    },
    series: [
      {
        type: 'bar',
        barMaxWidth: 24,
        data: groups.map(g => ({
          value: g.value,
          itemStyle: { color: barColor(g), borderRadius: [4, 4, 0, 0] },
        })),
      },
    ],
  };
  return <Chart option={option} ariaLabel={descriptor.title} />;
}

// ---------------------------------------------------------------------------
// composition — part-to-whole as ONE horizontal 100% bar (replaced donut)

const MAX_COMPOSITION_SEGMENTS = 5;

export function CompositionViz({ descriptor, data }: VizProps) {
  const { token } = theme.useToken();
  const palette = categoricalPalette(token);

  // At most 5 hues; the tail folds into "Other" rather than inventing a 6th
  // color (dataviz non-negotiable).
  const sorted = [...(data.groups ?? [])].sort((a, b) => b.value - a.value);
  const shown = sorted.slice(0, MAX_COMPOSITION_SEGMENTS - 1);
  const rest = sorted.slice(MAX_COMPOSITION_SEGMENTS - 1);
  const segments =
    rest.length > 1
      ? [...shown, { label: 'Other', value: rest.reduce((s, g) => s + g.value, 0) }]
      : sorted;
  const total = segments.reduce((sum, g) => sum + g.value, 0);

  if (total <= 0) {
    return (
      <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
        Nothing to show
      </Text>
    );
  }

  return (
    <Flex vertical gap={token.marginSM} justify="center" style={{ flex: 1 }}>
      {/* The bar: 2px surface gaps separate touching segments. */}
      <Flex gap={2} style={{ height: 12, borderRadius: 6, overflow: 'hidden' }} aria-label={descriptor.title} role="img">
        {segments.map((g, i) => (
          <div
            key={g.label}
            title={`${g.label}: ${formatMetricValue(g.value, data.unit)}`}
            style={{ width: `${(g.value / total) * 100}%`, background: palette[i % palette.length] }}
          />
        ))}
      </Flex>
      {/* Legend with values — identity is never color-alone. */}
      <Flex vertical gap={2}>
        {segments.map((g, i) => (
          <Flex key={g.label} align="center" gap={8}>
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: palette[i % palette.length],
                flexShrink: 0,
              }}
            />
            <Text ellipsis type="secondary" style={{ flex: 1, fontSize: token.fontSizeSM }}>
              {g.label}
            </Text>
            <Text className="tabular-nums" style={{ fontSize: token.fontSizeSM }}>
              {formatMetricValue(g.value, data.unit)}
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
}

// ---------------------------------------------------------------------------
// list — ranked entities; mono labels, quiet underline magnitude bars

export function ListViz({ descriptor, data }: VizProps) {
  const { token } = theme.useToken();
  const opts = descriptor.viz.options ?? {};
  const rows = (data.rows ?? []).slice(0, opts.limit ?? 5);
  const maxValue = Math.max(...rows.map(r => r.value ?? 0), 1);

  return (
    <Flex vertical style={{ height: '100%', overflowY: 'auto' }}>
      {rows.map((row, i) => {
        const accent = statusColor(row.status === 'neutral' ? undefined : row.status, token);
        const body = (
          <div
            style={{
              position: 'relative',
              padding: `${token.paddingXS}px ${token.paddingXXS}px`,
              borderTop: i > 0 ? `1px solid ${token.colorSplit}` : undefined,
            }}
          >
            <Flex align="center" gap={8}>
              {accent && (
                <span
                  style={{ width: 6, height: 6, borderRadius: '50%', background: accent, flexShrink: 0 }}
                />
              )}
              {/* Code-shaped labels (endpoints, tool ids, hashes) render in
                  mono — the console's vernacular; prose labels ("Members")
                  stay in the UI sans. */}
              <Text
                ellipsis
                style={{
                  flex: 1,
                  fontFamily: isCodeLabel(row.label) ? 'var(--font-mono)' : undefined,
                  fontSize: token.fontSizeSM,
                }}
              >
                {row.label}
              </Text>
              {row.meta && (
                <Text type="warning" style={{ fontSize: token.fontSizeSM }}>
                  {row.meta}
                </Text>
              )}
              {row.value != null && (
                <Text className="tabular-nums" style={{ fontSize: token.fontSizeSM }}>
                  {formatMetricValue(row.value, data.unit)}
                </Text>
              )}
            </Flex>
            {/* Magnitude as a 3px underline, not a background wash. */}
            {opts.showBar && row.value != null && (
              <div
                style={{
                  position: 'absolute',
                  left: token.paddingXXS,
                  bottom: 1,
                  height: 3,
                  width: `calc(${(row.value / maxValue) * 100}% - ${token.paddingXXS * 2}px)`,
                  background: token.colorPrimaryBg,
                  borderRadius: 2,
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        );
        return row.href ? (
          // Row → entity; the card itself may also be clickable (→ metric
          // detail), so row navigation must not bubble into it.
          <a
            key={`${row.label}-${i}`}
            href={row.href}
            style={{ color: 'inherit' }}
            onClick={e => e.stopPropagation()}
          >
            {body}
          </a>
        ) : (
          <div key={`${row.label}-${i}`}>{body}</div>
        );
      })}
      {rows.length === 0 && (
        <Text type="secondary" style={{ padding: token.paddingSM }}>
          Nothing to show
        </Text>
      )}
    </Flex>
  );
}

// ---------------------------------------------------------------------------

function Chart({ option, ariaLabel }: { option: EChartsOption; ariaLabel: string }) {
  const ref = useEChart(option);
  // minHeight 0 (not a floor value): the chart takes exactly the space the
  // card body leaves after header/padding — a floor here pushed columns out
  // the bottom of the 168px grid cell.
  return (
    <div
      ref={ref}
      role="img"
      aria-label={ariaLabel}
      style={{ flex: 1, minHeight: 0, width: '100%', overflow: 'hidden' }}
    />
  );
}

// Path separators, dots, underscores, or colons mark a label as an
// identifier rather than prose.
function isCodeLabel(label: string): boolean {
  return /[/._:]/.test(label);
}

function formatAxisTime(iso: string, hourly: boolean): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return hourly
    ? d.toLocaleTimeString(undefined, { hour: 'numeric' })
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
